/**
 * Auctioneer Login Page
 * Allows auctioneers to login with event-specific credentials
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

export default function AuctioneerLoginPage() {
  const navigate = useNavigate();
  const { eventId: urlEventId } = useParams();
  
  const [eventId, setEventId] = useState(urlEventId || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!eventId.trim()) {
      toast.error('Please enter the Event ID');
      return;
    }
    if (!username.trim()) {
      toast.error('Please enter your username');
      return;
    }
    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      // Verify auctioneer credentials
      const res = await axiosInstance.post(
        `/v1/events/${eventId.trim()}/auction/auctioneer/verify`,
        {},
        {
          headers: {
            'X-Auctioneer-Username': username.trim(),
            'X-Auctioneer-Password': password.trim(),
          },
        }
      );

      if (res.data.valid) {
        // Store auctioneer session
        const auctioneerSession = {
          eventId: eventId.trim(),
          username: username.trim(),
          password: password.trim(), // Needed for subsequent API calls
          auctionStatus: res.data.auction_status,
          loginTime: new Date().toISOString(),
          isAuctioneer: true,
        };
        localStorage.setItem('auctioneer_session', JSON.stringify(auctioneerSession));
        
        // Also set a simplified user object for role detection
        const auctioneerUser = {
          name: `Auctioneer (${username.trim()})`,
          role: 'auctioneer',
          roles: ['auctioneer'],
          isAuctioneer: true,
          eventId: eventId.trim(),
        };
        localStorage.setItem('user', JSON.stringify(auctioneerUser));
        localStorage.setItem('token', 'auctioneer_token'); // Placeholder token

        toast.success('Login successful! Redirecting to auction console...');
        
        // Redirect to the tournament auction page
        setTimeout(() => {
          navigate(`/tournaments/${eventId.trim()}`);
        }, 1000);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid credentials';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutExisting = () => {
    localStorage.removeItem('auctioneer_session');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    toast.info('Previous session cleared');
  };

  // Check if already logged in as auctioneer
  const existingSession = localStorage.getItem('auctioneer_session');
  const parsedSession = existingSession ? JSON.parse(existingSession) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B3A67] to-[#1a2544] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#3AAFA9] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ph-bold ph-microphone-stage text-3xl text-white"></i>
          </div>
          <h1 className="text-2xl font-black text-white">Auctioneer Login</h1>
          <p className="text-gray-400 mt-2">Enter your credentials to access the live auction console</p>
        </div>

        {/* Existing Session Warning */}
        {parsedSession && (
          <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <i className="ph-bold ph-warning text-amber-400 text-xl mt-0.5"></i>
              <div className="flex-1">
                <p className="text-amber-200 text-sm font-medium">Existing session found</p>
                <p className="text-amber-300/70 text-xs mt-1">
                  Event: {parsedSession.eventId?.slice(0, 8)}...
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => navigate(`/tournaments/${parsedSession.eventId}`)}
                    className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600"
                  >
                    Continue Session
                  </button>
                  <button
                    onClick={handleLogoutExisting}
                    className="px-3 py-1.5 bg-transparent border border-amber-500 text-amber-400 text-xs font-bold rounded-lg hover:bg-amber-500/20"
                  >
                    Clear & Login Fresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Event ID */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Event ID
            </label>
            <input
              type="text"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              placeholder="e.g., b5f364a3-4155-40f5-8915-604fd4769d63"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3AAFA9] focus:ring-2 focus:ring-[#3AAFA9]/20 outline-none text-sm"
              data-testid="auctioneer-event-id"
            />
            <p className="text-xs text-gray-400 mt-1">Ask your admin for the Event ID</p>
          </div>

          {/* Username */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toUpperCase())}
              placeholder="e.g., ABCDEF"
              maxLength={6}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3AAFA9] focus:ring-2 focus:ring-[#3AAFA9]/20 outline-none text-sm uppercase tracking-widest font-mono"
              data-testid="auctioneer-username"
            />
          </div>

          {/* Password */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3AAFA9] focus:ring-2 focus:ring-[#3AAFA9]/20 outline-none text-sm pr-12 font-mono"
                data-testid="auctioneer-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <i className={`ph-bold ${showPassword ? 'ph-eye-slash' : 'ph-eye'} text-lg`}></i>
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-[#3AAFA9] text-white font-bold hover:bg-[#2d8a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="auctioneer-login-btn"
          >
            {loading ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Verifying...
              </>
            ) : (
              <>
                <i className="ph-bold ph-sign-in"></i>
                Login to Auction
              </>
            )}
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            Don't have credentials?{' '}
            <span className="text-[#3AAFA9]">Contact your event administrator</span>
          </p>
        </div>

        {/* Back to Home */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-white text-sm flex items-center gap-1 mx-auto"
          >
            <i className="ph-bold ph-arrow-left"></i>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
